import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames/bind';
import TimerHelper from './TimerHelper';
import * as styles from './Timer.css';
import { dt } from '../../helpers/Utils/GetTailwindToken';

const cx = classNames.bind(styles);
/**
 * Generic Timer UI Component
 * Timer can be used for any deals which would expire within certain time
 *
 * More code Optimizations will be done for performance improvement as necessary
 */

class Timer extends Component {
    /**
     * Supported React properties
     * @type {Object}
     */
    static propTypes = {
        /**
         * initialTimeRemaining calculated with TimerHelper
         * @type {number} : optional prop
         */
        initialTimeRemaining: PropTypes.number,

        /**
         * A function to call when the countdown completes
         * @type {function} : optional prop
         */
        completeCallback: PropTypes.func,

        /**
         * A function to call each tick.
         * @type {function} : optional prop
         */
        tickCallback: PropTypes.func,

        /**
         * Suffix between Timer Values
         * @type {bool} : optional prop
         */
        suffix: PropTypes.bool,

        /**
         * text between Timer Values
         * @type {bool} : optional prop
         */
        text: PropTypes.oneOf(['on', 'off']),

        /**
         * themeConfig will help in applying a different theme
         * i.e, User can override the default theme by passing
         * themeConfig as props
         * @type {any}
         */
        themeConfig: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),

        /**
         * removeZeros when hour reaches 00
         * @type {bool} : optional prop
         */
        removeZeros: PropTypes.bool,

        /**
         * timerTextConfig for timer Text configuration
         * @type {Object} : optional prop
         */
        timerTextConfig: PropTypes.shape({
            h: PropTypes.string,
            m: PropTypes.string,
            s: PropTypes.string,
        }),

        /**
         * colorConfig for timer Color configuration
         * @type {Object} : optional prop
         */
        colorConfig: PropTypes.shape({
            textColor: PropTypes.string,
            suffixColor: PropTypes.string,
            digitBoxbgColor: PropTypes.string,
            digitBoxColor: PropTypes.string,
        }),

        /**
         * endTime for timer in Zone Format
         * @type {Date} : optional prop
         */
        endTime: PropTypes.instanceOf(Date),

        /**
         * startTime for timer in Zone Format
         * @type {Date} : optional prop
         */

        startTime: PropTypes.instanceOf(Date),

        /**
         * digitSplit for timer Numbers to split
         * @type {bool} : optional prop
         */
        digitSplit: PropTypes.bool,

        /**
         * animate for timer to animate digits
         * @type {bool} : optional prop
         */
        animate: PropTypes.bool,

        /**
         * display single dimentional time
         * @type {bool} : optional prop
         */

        displaySingleDimentionTimer: PropTypes.bool,

        /**
         * remove preceding zeros in time
         * @type {bool} : optional prop
         */

        removePrecedingZeros: PropTypes.bool,

        /**
         * increment minute by 1 in time
         * @type {bool} : optional prop
         */
        addAdditionalMinute: PropTypes.bool,

        /**
         * stickyTimer (CountUp Timer)
         * @type {bool} : optional prop
         */
        stickyTimer: PropTypes.bool,

        finalDayThreshold: PropTypes.number,

        today: PropTypes.instanceOf(Date),
        dayTimer: PropTypes.bool,
        eventBasedTimer: PropTypes.bool,
        dayText: PropTypes.string,
        enableSplitValue: PropTypes.bool,
    };

    /** @properties {Default set up} [optonal props set] */
    static defaultProps = {
        initialTimeRemaining: null,
        completeCallback: null,
        tickCallback: null,
        suffix: true,
        text: 'on',
        themeConfig: [],
        removeZeros: false,
        timerTextConfig: {
            h: 'hours',
            m: 'mins',
            s: 'secs',
        },
        endTime: null,
        digitSplit: false,
        colorConfig: {
            textColor: '',
            suffixColor: '',
            digitBoxbgColor: '',
            digitBoxColor: '',
        },
        animate: false,
        displaySingleDimentionTimer: false,
        removePrecedingZeros: false,
        addAdditionalMinute: false,
        stickyTimer: false,
        startTime: null,
        finalDayThreshold: 24,
        today: null,
        dayTimer: false,
        eventBasedTimer: false,
        dayText: 'DAYS',
        enableSplitValue: true,
    };

    /**
     * Gets the theme to use
     * @return {[Object]} [theming classes]
     */
    static getThemeConfig(themeConfig) {
        const timerStyle = dt(['overflow-auto']);
        const timerListItemStyle = dt([
            'float-left',
            'm-1',
            'text-center',
            'text-large',
            'font-bold',
        ]);
        const timerTextStyle = dt(['block', '-mt-1.5', 'text-xsmall']);
        return {
            timerContainerClass: styles.timerContainer,
            timerWrapperClass: [timerStyle, styles.timer],
            timerListItemClass: [timerListItemStyle, styles.timerListItem],
            timerNumberClass: styles.timerNumber,
            timerTextClass: [timerTextStyle, styles.timerText],
            timerSuffixClass: [timerListItemStyle, styles.suffix],
            timerDigitClass: styles.timerDigit,
            timerNumberWidthClass: styles.timerNumberWidth,
            timerHourWidthClass: styles.timerHourWidth,
            ...themeConfig,
        };
    }

    constructor(props) {
        super(props);
        const {
            startTime,
            endTime,
            finalDayThreshold,
            today,
            dayTimer,
            eventBasedTimer,
        } = this.props;
        const timeRemaining =
            props.initialTimeRemaining ||
            (props.stickyTimer
                ? TimerHelper.getremainingTimerDetails(
                      startTime,
                      endTime,
                      finalDayThreshold,
                      today,
                      dayTimer,
                      eventBasedTimer
                  )
                : TimerHelper.getTimeRemaining(endTime));
        this.state = {
            timeRemaining: timeRemaining.total > 0 ? timeRemaining.total : 0,
            hours: null,
            minutes: null,
            seconds: null,
            hasFlipped: false,
        };

        this.getFormattedTime = this.getFormattedTime.bind(this);
        this.tick = this.tick.bind(this);
        this.isComponentMounted = false;
        this.getTimer = this.getTimer.bind(this);
        this.themeConfig = Timer.getThemeConfig(props.themeConfig);
        this.timeRemaining = timeRemaining.total;
    }

    componentDidMount() {
        this.isComponentMounted = true;

        if (this.timeRemaining > 0) {
            this.interval = setInterval(() => this.tick(), 1000);
        }
    }

    componentWillUnmount() {
        this.clearIntervals();
    }

    getFormattedTime(milliseconds) {
        if (this.props.stickyTimer) {
            const {
                startTime,
                endTime,
                finalDayThreshold,
                today,
                dayTimer,
                eventBasedTimer,
            } = this.props;
            return TimerHelper.getFormattedRemainingTime(
                startTime,
                endTime,
                finalDayThreshold,
                today,
                dayTimer,
                eventBasedTimer
            );
        }
        return TimerHelper.getFormattedTime(
            milliseconds,
            this.props.removePrecedingZeros,
            this.props.addAdditionalMinute,
            this.props.endTime
        );
    }

    /**
     * Flip Animation Template
     */

    getAnimateTemplate(
        digits,
        timerDigitClass,
        digitBoxStyle,
        prefix,
        timerNumberClass,
        timerAnimateClass
    ) {
        // default flip class
        let flipClass = cx('timerClockDigitInner', timerAnimateClass);
        let flipNumberClass = flipClass;
        const { hasFlipped } = this.state; // picking flipped state

        // Prev and Next Html flipping
        let prevNum1 = (
            <div className={cx('timerPrevNumber', timerNumberClass)}>
                {' '}
                <span>{digits[0]}</span>{' '}
            </div>
        );
        let nextNum1 = (
            <div className={cx('timerNextNumber', timerNumberClass)}>
                {' '}
                <span>{digits[0] - 1}</span>{' '}
            </div>
        );

        let nextNum2 = (
            <div className={cx('timerNextNumber', timerNumberClass)}>
                {' '}
                <span>{digits[1] - 1}</span>{' '}
            </div>
        );
        let prevNum2 = (
            <div className={cx('timerPrevNumber', timerNumberClass)}>
                {' '}
                <span>{digits[1]}</span>{' '}
            </div>
        );

        // condn to check and set flip class for 1st digit
        /* istanbul ignore next */
        if (this[`secFlipNumber${prefix}1`] > -1 && digits[0] !== this[`secFlipNumber${prefix}1`]) {
            flipNumberClass = cx('timerClockDigitInner', 'timerFlip', timerAnimateClass);
            prevNum1 = (
                <div className={cx('timerPrevNumber', timerNumberClass)}>
                    {' '}
                    <span>{digits[0] !== 5 ? digits[0] + 1 : 0}</span>
                </div>
            );
            nextNum1 = (
                <div className={cx('timerNextNumber', timerNumberClass)}>
                    {' '}
                    <span>{digits[0]}</span>{' '}
                </div>
            );
            this[`secFlipNumber${prefix}1`] = digits[0];
        }

        // condn to check and set flip class for 2nd digit
        /* istanbul ignore next */
        if (
            hasFlipped &&
            this[`secFlipNumber${prefix}2`] > -1 &&
            digits[1] !== this[`secFlipNumber${prefix}2`]
        ) {
            flipClass = cx('timerClockDigitInner', 'timerFlip', timerAnimateClass);
            nextNum2 = (
                <div className={cx('timerNextNumber', timerNumberClass)}>
                    {' '}
                    <span>{digits[1]}</span>{' '}
                </div>
            );
            prevNum2 = (
                <div className={cx('timerPrevNumber', timerNumberClass)}>
                    {' '}
                    <span>{digits[1] !== 9 ? digits[1] + 1 : 0}</span>{' '}
                </div>
            );
            this[`secFlipNumber${prefix}2`] = digits[1];
        }

        // setting flip values to digits
        this[`secFlipNumber${prefix}1`] = digits[0];
        this[`secFlipNumber${prefix}2`] = digits[1];

        // final rendered animataHTML
        const timerClockDigitStyle = dt(['float-left', 'overflow-hidden', 'relative']);
        return (
            <div>
                <div className={cx('timerClockDigit', timerClockDigitStyle, timerDigitClass)}>
                    <div className={cx(flipNumberClass)} style={digitBoxStyle}>
                        {prevNum1}
                        {nextNum1}
                    </div>
                </div>
                <div className={cx('timerClockDigit', timerClockDigitStyle, timerDigitClass)}>
                    <div className={cx(flipClass)} style={digitBoxStyle}>
                        {prevNum2}
                        {nextNum2}
                    </div>
                </div>
            </div>
        );
    }

    getTimer(hours, minutes, seconds) {
        // Extracting classnames from theme config
        const {
            timerWrapperClass,
            timerListItemClass,
            timerNumberClass,
            timerTextClass,
            timerSuffixClass,
            timerDigitClass,
            timerAnimateClass,
            timerNumberWidthClass,
            timerHourWidthClass,
        } = this.themeConfig;

        // destrucuring props
        const {
            textColor,
            suffixColor,
            digitBoxbgColor = '',
            digitBoxColor = '',
        } = this.props.colorConfig;

        // Coloring timer setup.
        const textStyle = textColor ? { color: textColor } : null;
        const suffixStyle = suffixColor ? { color: suffixColor } : null;
        const digitBoxStyle =
            digitBoxbgColor || digitBoxColor
                ? { backgroundColor: digitBoxbgColor, color: digitBoxColor }
                : null;

        // Suffix handling in here
        let suffixHtmlHours = (
            <div className={timerSuffixClass} style={suffixStyle}>
                {' '}
                :{' '}
            </div>
        );
        let suffixHtmlMinutes = (
            <div className={timerSuffixClass} style={suffixStyle}>
                {' '}
                :{' '}
            </div>
        );
        if (!this.props.suffix) {
            suffixHtmlHours = '';
            suffixHtmlMinutes = '';
        }
        let hoursNumberHtml = (
            <span className={cx(timerNumberWidthClass, timerHourWidthClass)} style={digitBoxStyle}>
                {hours}
            </span>
        );
        let minutesNumberHtml = (
            <span className={timerNumberWidthClass} style={digitBoxStyle}>
                {minutes}
            </span>
        );
        let secondsNumberHtml = (
            <span className={timerNumberWidthClass} style={digitBoxStyle}>
                {seconds}
            </span>
        );

        /** hoursNumberHtml */
        if (this.props.digitSplit) {
            const hoursArr = TimerHelper.getDigitSplit(hours);
            const minutesArr = TimerHelper.getDigitSplit(minutes);
            const secondsArr = TimerHelper.getDigitSplit(seconds);

            // digit split HTML for timer
            hoursNumberHtml = (
                <span>
                    <span className={timerDigitClass} style={digitBoxStyle}>
                        {hoursArr[0]}
                    </span>
                    <span className={timerDigitClass} style={digitBoxStyle}>
                        {hoursArr[1]}
                    </span>
                </span>
            );
            minutesNumberHtml = (
                <span>
                    <span className={timerDigitClass} style={digitBoxStyle}>
                        {minutesArr[0]}
                    </span>
                    <span className={timerDigitClass} style={digitBoxStyle}>
                        {minutesArr[1]}
                    </span>
                </span>
            );
            secondsNumberHtml = (
                <span>
                    <span className={timerDigitClass} style={digitBoxStyle}>
                        {secondsArr[0]}
                    </span>
                    <span className={timerDigitClass} style={digitBoxStyle}>
                        {secondsArr[1]}
                    </span>
                </span>
            );

            // if prop animate is true then we need to do this
            if (this.props.animate) {
                hoursNumberHtml = this.getAnimateTemplate(
                    hoursArr,
                    timerDigitClass,
                    digitBoxStyle,
                    'H',
                    timerNumberClass,
                    timerAnimateClass
                );
                minutesNumberHtml = this.getAnimateTemplate(
                    minutesArr,
                    timerDigitClass,
                    digitBoxStyle,
                    'M',
                    timerNumberClass,
                    timerAnimateClass
                );
                secondsNumberHtml = this.getAnimateTemplate(
                    secondsArr,
                    timerDigitClass,
                    digitBoxStyle,
                    'S',
                    timerNumberClass,
                    timerAnimateClass
                );
            }
        }

        /** Text to show or not starts */
        let hoursTextHtml = (
            <span className={timerTextClass} style={textStyle}>
                {this.props.timerTextConfig.h}{' '}
            </span>
        );
        let minsTextHtml = (
            <span className={timerTextClass} style={textStyle}>
                {this.props.timerTextConfig.m}{' '}
            </span>
        );
        let secsTextHtml = (
            <span className={timerTextClass} style={textStyle}>
                {this.props.timerTextConfig.s}{' '}
            </span>
        );

        if (this.props.text === 'off') {
            hoursTextHtml = '';
            minsTextHtml = '';
            secsTextHtml = '';
        }
        if (this.props.enableSplitValue) {
            hoursTextHtml = ':';
            minsTextHtml = ':';
            secsTextHtml = '';
        }
        /** Text to show or not ends */

        // start of populating hours,mins,secs HTML
        let hoursHtml = (
            <div className={timerListItemClass}>
                <h4 className={timerNumberClass}>
                    {hoursNumberHtml}
                    {hoursTextHtml}
                </h4>
            </div>
        );
        let minsHtml = (
            <div className={timerListItemClass}>
                <h4 className={timerNumberClass}>
                    {minutesNumberHtml}
                    {minsTextHtml}
                </h4>
            </div>
        );
        const secsHtml = (
            <div className={timerListItemClass}>
                <h4 className={timerNumberClass}>
                    {secondsNumberHtml}
                    {secsTextHtml}
                </h4>
            </div>
        );

        // removing zeros
        if (this.props.removeZeros) {
            if (hours === '00') {
                hoursHtml = '';
                suffixHtmlHours = '';
            }
            /* istanbul ignore next */
            if (hours === '00' && minutes === '00') {
                minsHtml = '';
                suffixHtmlMinutes = '';
            }
        }

        // end of Populating hours,mins,secs HTML

        const displaySingleDimentionTimerHtml = [];
        if (this.props.displaySingleDimentionTimer) {
            const hrs = parseInt(hours, 0);
            const min = parseInt(minutes, 0);
            if (hrs < 24 && hrs > 0) {
                displaySingleDimentionTimerHtml.push(<div key="hoursHtml">{hoursHtml}</div>);
            } else if (min < 60 && hrs < 1 && min > 1) {
                displaySingleDimentionTimerHtml.push(<div key="minsHtml">{minsHtml}</div>);
            } else {
                displaySingleDimentionTimerHtml.push(
                    <div key="timerListItem" className={timerListItemClass}>
                        <h4 className={timerNumberClass}>
                            <span>
                                <span className={timerDigitClass} style={digitBoxStyle}>
                                    1
                                </span>
                            </span>
                            <span className={timerTextClass} style={textStyle}>
                                {' '}
                                {this.props.timerTextConfig.m}{' '}
                            </span>
                        </h4>
                    </div>
                );
            }
        } else {
            const hrs = parseInt(hours, 0);
            if (hrs >= 48) {
                const { endTime, today } = this.props;
                const remainingDays = (
                    <span className={cx('timerDayText')}>{this.props.dayText}</span>
                );
                const daysLeft = (
                    <span className={cx('timerDayLeft')}>
                        {TimerHelper.getDaysLeft(endTime, today)}
                    </span>
                );
                displaySingleDimentionTimerHtml.push(
                    <div key="multiTimerDayHtml">
                        <div className={timerListItemClass}>
                            <h4 className={timerNumberClass}>
                                {daysLeft}
                                {remainingDays}
                            </h4>
                        </div>
                    </div>
                );
            } else {
                displaySingleDimentionTimerHtml.push(
                    <div key="multiTimerHoursHtml">
                        {hoursHtml}
                        {suffixHtmlHours}
                        {minsHtml}
                        {suffixHtmlMinutes}
                        {secsHtml}
                    </div>
                );
            }
        }

        return <div className={timerWrapperClass}>{displaySingleDimentionTimerHtml}</div>;
    }

    clearIntervals() {
        clearInterval(this.interval);
    }

    flip() {
        this.setState({
            hasFlipped: false,
        });
    }

    tick() {
        const {
            startTime,
            endTime,
            finalDayThreshold,
            stickyTimer,
            animate,
            today,
            dayTimer,
            eventBasedTimer,
        } = this.props;
        // const remainingTime = this.state.timeRemaining - 1000;
        // We Do this because we need the Timer to display right value when system goes to sleep mode
        const remainingTime = stickyTimer
            ? TimerHelper.getremainingTimerDetails(
                  startTime,
                  endTime,
                  finalDayThreshold,
                  today,
                  dayTimer,
                  eventBasedTimer
              )
            : TimerHelper.getTimeRemaining(endTime);
        /* istanbul ignore next */
        if (this.isMountedComponent() && remainingTime.total > 0) {
            this.setState({ timeRemaining: remainingTime.total, hasFlipped: true });
        } else {
            // clear the interval , can be assigned to a state
            this.clearIntervals();

            /** completeCallBack is called when timer completes */
            if (this.props.completeCallback) {
                this.props.completeCallback();
            }
            return;
        }

        // Reset the timerFlip class from animated digits
        // animate only on request.
        /* istanbul ignore next */
        if (animate) {
            setTimeout(() => this.flip(), 500);
        }

        /**
         * call back execution
         */
        /* istanbul ignore next */
        if (this.props.tickCallback) {
            this.props.tickCallback(remainingTime.total);
        }
    }

    isMountedComponent() {
        return this.isComponentMounted;
    }

    render() {
        const milliseconds = this.state.timeRemaining;
        const remainingTime = this.getFormattedTime(milliseconds);

        const { hours, minutes, seconds } = remainingTime;
        return (
            <div className={this.themeConfig.timerContainerClass}>
                {this.getTimer(hours, minutes, seconds)}
            </div>
        );
    }
} // end of Timer

export default Timer;
export { TimerHelper };
